import { useState } from 'react';
import styled from 'styled-components';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import P from '../../components/texts/p/P';
import Input from '../../components/input/Input';
import DatePicker from '../../components/datepicker/DatePicker';
import DropDown from '../../components/dropdown/DropDown';
import Icon from '../../components/icon/Icon';
import Checkbox from '../../components/checkBox/CheckBox';
import { MonetizationFormData, FormValues } from '../../pages/MonetizationNew';
import { useFiscalIdConfig } from '../../hooks/useFiscalIdConfig';
import { getMonetizationOperationType } from '../../utils/monetizationExecution';
import theme from '../../styles/theme';

interface GeneralSectionProps {
  formData: MonetizationFormData;
  formValues: FormValues;
  updateFormValues: (updates: Partial<FormValues>) => void;
  prefilledMode?: boolean;
  showAvalSindicado?: boolean;
  lockedSyndicated?: boolean;
}

const AVAL_SINDICADO_CHECKBOX_STYLE = {
  backgroundColor: '#03BAA1',
  borderColor: '#03BAA1',
  checkmarkColor: '#FFFFFF',
  uncheckedBackgroundColor: '#FFFFFF',
};

const FechaCargaField = ({ createdAt }: { createdAt?: string }) => (
  <DatePicker
    label="Fecha de carga *"
    defaultValue={createdAt || new Date().toISOString().split('T')[0]}
    onChange={() => {}}
    disabled={true}
    displayFormat="DD/MM/AA"
  />
);

const AVAL_SINDICADO_CARD_STYLE = {
  display: 'flex' as const,
  flexDirection: 'column' as const,
  gap: '8px',
  minWidth: 0,
  width: '100%',
  backgroundColor: '#E6F5F0',
  border: '1.5px dashed #03BAA1',
  borderRadius: '8px',
  padding: '22px 16px 12px',
  boxSizing: 'border-box' as const,
};

const ReadOnlyTextWrap = styled.div`
  width: 100%;
  min-width: 0;

  & input {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const InstrumentGridCell = styled.div`
  min-width: 0;
`;

const InstrumentGridTypeLetterCell = styled(InstrumentGridCell)`
  align-self: end;
`;

const SyndicatedNumberWrap = styled.div<{ $error?: boolean }>`
  width: 100%;

  & > div > div:has(> input) {
    padding: 0;
    box-shadow: none !important;
    border: none !important;
    background-color: transparent;
  }

  & input {
    padding: 8px 12px;
    box-sizing: border-box;
    border-radius: ${theme.borderRadius['border-radius-3']};
    border: 1px solid
      ${({ $error }) =>
        $error ? theme.colors.danger.border.regular : theme.colors.neutral.border.regular};
    background-color: ${theme.colors.neutral.bg.inverted};
  }
`;

const AvalSindicadoControls = ({
  formValues,
  updateFormValues,
  lockedSyndicated = false,
}: {
  formValues: FormValues;
  updateFormValues: (updates: Partial<FormValues>) => void;
  lockedSyndicated?: boolean;
}) => {
  const [touched, setTouched] = useState(false);
  const syndicatedNumberMissing =
    !!formValues.is_syndicated && !formValues.syndicated_number?.trim();
  const showError = syndicatedNumberMissing && (!lockedSyndicated || touched);

  return (
    <div data-testid="aval-sindicado-controls" style={AVAL_SINDICADO_CARD_STYLE}>
      <Checkbox
        id="is_syndicated"
        size="md"
        labelWeight="bold"
        customStyle={AVAL_SINDICADO_CHECKBOX_STYLE}
        checked={!!formValues.is_syndicated}
        disabled={lockedSyndicated}
        onChange={(checked: boolean) => {
          if (lockedSyndicated) return;
          updateFormValues({
            is_syndicated: !!checked,
            syndicated_number: checked ? formValues.syndicated_number : '',
          });
        }}
      >
        Aval sindicado
      </Checkbox>
      {!lockedSyndicated ? (
        <P
          withMargin={false}
          variant="neutral"
          type="4"
          modifier="regular"
          style={{ fontStyle: 'italic', marginLeft: '28px', lineHeight: 1.3 }}
        >
          Marcar si el aval es sindicado
        </P>
      ) : null}
      {formValues.is_syndicated ? (
        <>
          <SyndicatedNumberWrap $error={showError}>
            <Input
              label="Nº sindicado *"
              value={formValues.syndicated_number || ''}
              onChange={(e) => updateFormValues({ syndicated_number: e.target.value })}
              onBlur={() => setTouched(true)}
              size="md"
              maxLength={50}
              inputStatus={showError ? 'error' : 'normal'}
            />
          </SyndicatedNumberWrap>
          {showError ? (
            <P
              withMargin={false}
              variant="danger"
              type="4"
              modifier="regular"
              style={{ marginTop: '2px' }}
            >
              El Nº sindicado es obligatorio
            </P>
          ) : null}
        </>
      ) : null}
    </div>
  );
};

const GeneralSection = ({
  formData,
  formValues,
  updateFormValues,
  prefilledMode = false,
  showAvalSindicado = false,
  lockedSyndicated = false,
}: GeneralSectionProps) => {
  const fiscalId = useFiscalIdConfig();
  const getDisplayName = (gt: { name: string; name_end_user?: string }) =>
    gt.name_end_user || gt.name;

  const operationType = getMonetizationOperationType(formData.product_line_label);
  const isChequeOperation = operationType === 'cheque';
  const isLoanOperation = operationType === 'préstamo';
  const isPromissoryNoteOperation = operationType === 'pagaré';
  const isOperationTypeReadOnly =
    prefilledMode || isChequeOperation || isLoanOperation || isPromissoryNoteOperation;

  const guaranteeTypeItems = formData.guarantee_types?.map(guaranteeType => ({
    label: getDisplayName(guaranteeType),
    disabled: false
  })) || [];

  const getSelectedGuaranteeTypeName = () => {
    if (!formValues.guarantee_type_id) return 'Seleccionar';
    const selectedType = formData.guarantee_types?.find(gt => gt.id === formValues.guarantee_type_id);
    if (selectedType) return getDisplayName(selectedType);
    return `Tipo #${formValues.guarantee_type_id} (no disponible)`;
  };

  const handleGuaranteeTypeChange = (selectedLabel: string) => {
    if (prefilledMode) return;

    const guaranteeType = formData.guarantee_types?.find(gt => getDisplayName(gt) === selectedLabel);
    if (guaranteeType) {
      updateFormValues({ guarantee_type_id: guaranteeType.id });
    }
  };

  const selectedGuaranteeType = formData.guarantee_types?.find(
    (gt) => gt.id === formValues.guarantee_type_id
  );

  const guaranteeLabelItems = Array.from(
    new Set(formData.guarantee_types?.map((gt) => gt.label).filter(Boolean) || [])
  ).map((label) => ({ label }));

  const handleGuaranteeLabelChange = (selectedLabel: string) => {
    if (prefilledMode) return;

    const guaranteeType =
      formData.guarantee_types?.find((gt) => gt.label === selectedLabel) ||
      formData.guarantee_types?.find((gt) => gt.id === formValues.guarantee_type_id);
    if (guaranteeType) {
      updateFormValues({ guarantee_type_id: guaranteeType.id });
    }
  };

  const getReadOnlyOperationTypeDisplay = () => {
    if (isLoanOperation) {
      return formData.product_line_label || getSelectedGuaranteeTypeName();
    }
    return getSelectedGuaranteeTypeName();
  };

  const operationTypeDisplay = isOperationTypeReadOnly
    ? getReadOnlyOperationTypeDisplay()
    : getSelectedGuaranteeTypeName();

  const usesCompactGeneralLayout = prefilledMode;

  const instrumentGridColumns = lockedSyndicated
    ? '2fr 1fr 1.5fr auto'
    : '1fr 2fr 1fr 1.5fr auto';

  const usesInstrumentGeneralGrid =
    isChequeOperation || isLoanOperation || isPromissoryNoteOperation;

  const showAvalNumber = usesInstrumentGeneralGrid
    ? !lockedSyndicated
    : !prefilledMode && !lockedSyndicated;

  const avalNumberDisplay =
    formValues.id_global != null
      ? String(formValues.id_global)
      : formValues.product_line_id || '';

  const row1Columns = prefilledMode || lockedSyndicated || usesCompactGeneralLayout
    ? 'repeat(4, minmax(0, 1fr))'
    : '1fr 2fr 1fr 1.5fr auto';

  const row2Columns = usesCompactGeneralLayout
    ? showAvalSindicado
      ? '1fr 1fr 1fr 1.5fr'
      : 'repeat(4, minmax(0, 1fr))'
    : showAvalSindicado
      ? '1fr 1.4fr 1.5fr'
      : '1fr 1.5fr 1fr 1fr';

  const renderInstrumentGeneralGrid = (gridTestId: string, includeSyndicated: boolean) => (
    <div
      data-testid={gridTestId}
      style={{
        display: 'grid',
        gridTemplateColumns: instrumentGridColumns,
        gap: '16px',
        rowGap: '24px',
        alignItems: includeSyndicated && showAvalSindicado ? 'start' : 'end',
      }}
    >
      {showAvalNumber && (
        <InstrumentGridCell style={{ gridColumn: 1, gridRow: 1 }}>
          <Input
            label="Número de aval"
            value={avalNumberDisplay}
            readOnly
            size="md"
            modifier="weak"
          />
        </InstrumentGridCell>
      )}
      <InstrumentGridCell style={{ gridColumn: showAvalNumber ? 2 : 1, gridRow: 1 }}>
        <Card position='relative'>
          <ReadOnlyTextWrap>
            <Input
              label="Razón social"
              value={formData.company_name}
              readOnly
              size="md"
              modifier="weak"
            />
          </ReadOnlyTextWrap>
          {(prefilledMode || lockedSyndicated) && (
            <Card position='absolute' style={{ right: '8px', top: '32px' }}>
              <Icon name='lock_fill' variant='neutral' size='sm' />
            </Card>
          )}
        </Card>
      </InstrumentGridCell>
      <InstrumentGridCell style={{ gridColumn: showAvalNumber ? 3 : 2, gridRow: 1 }}>
        <Card position='relative'>
          <Input
            label={fiscalId.label}
            value={formData.cuit}
            readOnly
            size="md"
            modifier="weak"
          />
          {(prefilledMode || lockedSyndicated) && (
            <Card position='absolute' style={{ right: '8px', top: '32px' }}>
              <Icon name='lock_fill' variant='neutral' size='sm' />
            </Card>
          )}
        </Card>
      </InstrumentGridCell>
      <InstrumentGridCell style={{ gridColumn: showAvalNumber ? 4 : 3, gridRow: 1 }}>
        <Card position='relative'>
          <ReadOnlyTextWrap>
            <Input
              label="Tipo de operación"
              value={operationTypeDisplay}
              readOnly
              size="md"
              modifier="weak"
            />
          </ReadOnlyTextWrap>
          {(prefilledMode || lockedSyndicated) && (
            <Card position='absolute' style={{ right: '8px', top: '32px' }}>
              <Icon name='lock_fill' variant='neutral' size='sm' />
            </Card>
          )}
        </Card>
      </InstrumentGridCell>
      <InstrumentGridTypeLetterCell style={{ gridColumn: showAvalNumber ? 5 : 4, gridRow: 1 }}>
        <Input
          value={selectedGuaranteeType?.type || ''}
          readOnly
          size="xs"
          modifier="weak"
        />
      </InstrumentGridTypeLetterCell>
      <InstrumentGridCell style={{ gridColumn: 1, gridRow: 2 }}>
        <DropDown
          id="guarantee_category"
          label="Tipo de garantía *"
          title={selectedGuaranteeType?.label || 'Seleccionar'}
          items={guaranteeLabelItems}
          onChange={handleGuaranteeLabelChange}
          size="md"
          disabled={prefilledMode || guaranteeLabelItems.length === 0}
        />
      </InstrumentGridCell>
      <InstrumentGridCell style={{ gridColumn: 2, gridRow: 2 }}>
        <FechaCargaField createdAt={formValues.created_at} />
      </InstrumentGridCell>
      {includeSyndicated && showAvalSindicado ? (
        <InstrumentGridCell style={{ gridColumn: '3 / 6', gridRow: 2 }}>
          <AvalSindicadoControls
            formValues={formValues}
            updateFormValues={updateFormValues}
            lockedSyndicated={lockedSyndicated}
          />
        </InstrumentGridCell>
      ) : null}
    </div>
  );

  return (
    <Card margin={{ bottom: '4' }}>
      <div
        style={{ backgroundColor: '#E6F5F0', padding: '12px 16px', borderRadius: '8px', margin: '0 0 16px 0' }}
      >
        <H variant="secondary-2" type='4' weight='bold' modifier='strong' withMargin={false}>
          General
        </H>
      </div>

      {isChequeOperation ? (
        renderInstrumentGeneralGrid('general-section-cheque-grid', false)
      ) : isLoanOperation ? (
        renderInstrumentGeneralGrid('general-section-loan-grid', true)
      ) : isPromissoryNoteOperation ? (
        renderInstrumentGeneralGrid('general-section-pagare-grid', false)
      ) : (
        <>
      {/* Fila 1 */}
      <div
        data-testid="general-section-row-1"
        style={{ display: 'grid', gridTemplateColumns: row1Columns, gap: '16px', marginBottom: '24px', alignItems: 'end' }}
      >
        {showAvalNumber && (
          <Input
            label="Número de aval"
            value={avalNumberDisplay}
            readOnly
            size="md"
            modifier="weak"
          />
        )}
        <Card position='relative'>
          <ReadOnlyTextWrap>
            <Input
              label="Razón social"
              value={formData.company_name}
              readOnly
              size="md"
              modifier="weak"
            />
          </ReadOnlyTextWrap>
          {(prefilledMode || lockedSyndicated) && (
            <Card position='absolute' style={{ right: '8px', top: '32px' }}>
              <Icon name='lock_fill' variant='neutral' size='sm' />
            </Card>
          )}
        </Card>
        <Card position='relative'>
          <Input
            label={fiscalId.label}
            value={formData.cuit}
            readOnly
            size="md"
            modifier="weak"
          />
          {(prefilledMode || lockedSyndicated) && (
            <Card position='absolute' style={{ right: '8px', top: '32px' }}>
              <Icon name='lock_fill' variant='neutral' size='sm' />
            </Card>
          )}
        </Card>
        {isOperationTypeReadOnly ? (
          <Card position='relative'>
            <ReadOnlyTextWrap>
              <Input
                label="Tipo de operación"
                value={operationTypeDisplay}
                readOnly
                size="md"
                modifier="weak"
              />
            </ReadOnlyTextWrap>
            {(prefilledMode || lockedSyndicated) && (
              <Card position='absolute' style={{ right: '8px', top: '32px' }}>
                <Icon name='lock_fill' variant='neutral' size='sm' />
              </Card>
            )}
          </Card>
        ) : (
          <div>
            <DropDown
              id="guarantee_type"
              label="Tipo de operación"
              title={getSelectedGuaranteeTypeName()}
              items={guaranteeTypeItems}
              onChange={handleGuaranteeTypeChange}
              size="md"
              marginTop="4px"
              disabled={guaranteeTypeItems.length === 0}
            />
            {guaranteeTypeItems.length === 0 && (
              <P type="4" variant="error" withMargin={false}>
                No hay tipos de operación disponibles para este instrumento
              </P>
            )}
          </div>
        )}
        {!isOperationTypeReadOnly && (
          <div>
            <Input
              value={selectedGuaranteeType?.type || ''}
              readOnly
              size="xs"
              modifier="weak"
            />
          </div>
        )}
      </div>

      {/* Fila 2 */}
      <div
        data-testid="general-section-row-2"
        style={{
          display: 'grid',
          gridTemplateColumns: row2Columns,
          gap: '16px',
          alignItems: showAvalSindicado && !isChequeOperation ? 'start' : 'end',
        }}
      >
        <>
        <Input
          label="Tipo de garantía"
          value={selectedGuaranteeType?.label || ''}
          readOnly
          size="md"
          modifier="weak"
        />
        {usesCompactGeneralLayout ? (
          <>
            <FechaCargaField createdAt={formValues.created_at} />
            <Card position='relative'>
              <Input
                label="Divisa"
                value={formData.currency}
                readOnly
                size="md"
                modifier="weak"
              />
              {prefilledMode && (
                <Card position='absolute' style={{ right: '8px', top: '32px' }}>
                  <Icon name='lock_fill' variant='neutral' size='sm' />
                </Card>
              )}
            </Card>
            {showAvalSindicado ? (
              <AvalSindicadoControls
                formValues={formValues}
                updateFormValues={updateFormValues}
                lockedSyndicated={lockedSyndicated}
              />
            ) : (
              <div />
            )}
          </>
        ) : (
          <>
            <FechaCargaField createdAt={formValues.created_at} />
            {showAvalSindicado ? (
              <AvalSindicadoControls
                formValues={formValues}
                updateFormValues={updateFormValues}
                lockedSyndicated={lockedSyndicated}
              />
            ) : (
              <div />
            )}
            {!showAvalSindicado && <div />}
          </>
        )}
        </>
      </div>
        </>
      )}
    </Card>
  );
};

export default GeneralSection;
